import { DateUtil } from "../utils/DateUtil.js";
import { VOBlogKind, VOSensor, VOSensorCollection, VOExpeditionPhase, VORoutePhase } from "./DataStructure.js";

export interface SensorEntry {
    date: Date;
    dateKey: string;
    time: number;
    index: number;
    lat: number;
    lng: number;
    x: number;
    y: number;
    distance: number;
    wind: number;
    temperature: number;
    water_temperature: number;
    pathData?: SVGPathElement[];
    pathDataRest?: SVGPathElement[];
    phase:VORoutePhase;
}

export type SensorGetMode = "average" | "first" | "nearest";

export class Sensor {
    public onChange = e5.Signal.create();

    public data: VOSensorCollection;
    public entries: SensorEntry[] = [];

    private _lookup: any = {};
    private _lookupNearest: any = {};
    private _lookupFirst: any = {};
    private _lookupAverage: any = {};
    private _updateInterval: any = null;

    constructor(data: VOSensorCollection = null) {
        if (data)
            this.fromData(data);
    }

    public get kind(): VOBlogKind {
        return this.data ? this.data.data.kind : "mosaic";
    }

    public get days(): number {
        return Math.abs(DateUtil.dayDiff(this.entries[this.entries.length - 1].date, this.entries[0].date)) + 1;
    }

    /**
     * Auto-update the sensor data.
     * 
     * @param url The url to update the sensor data.
     * @param interval The interval in minutes.
     */
    public autoUpdate(url: string, interval: number = 5): void {
        clearInterval(this._updateInterval);
        this._updateInterval = setInterval(() => {
            this.update(url);
        }, interval * 1000 * 60);
    }

    /**
     * Update the sensor data.
     * 
     * @param url The url to update the sensor data.
     */
    public update(url: string): void {
        url = e5.Url.params.set("sensorid", this.data ? this.data.id + "" : "1", url);
        $.getJSON(url, (data: any) => {
            //Error getting data
            if (!data)
                return;
            //The data is up-to-date
            if (data.current) {
                e5.console.log("Live data is up-to-date");
                return;
            }

            var sensorCollection: VOSensorCollection = data;

            //No data given
            if (!sensorCollection.data)
                return;

            this.fromData(sensorCollection);
            e5.console.log("Live data updated!");
        });
    }

    public getEntry(date: Date, mode: SensorGetMode): SensorEntry {
        if (this.kind === "nansen") {
            if (date.getFullYear() < 1900) {
                console.warn("You try get a nansen sensor entry with a date in nansen-time. Use mosaic-time instead!", DateUtil.toStringReadable(date, "de"));
                date = DateUtil.nansenToMosaic(date);
            }
        }

        if (mode === "nearest")
            return this.getNearest(date);
        if (mode === "average")
            return this.getAverage(date);
        if (mode === "first")
            return this.getFirst(date);
    }

    public getEntryForEachDay(mode: SensorGetMode): SensorEntry[] {

        var start = this.entries[0];
        var end = this.entries[this.entries.length - 1];

        var startDate = new Date(start.date);
        var days = Math.abs(DateUtil.dayDiff(startDate, end.date));

        var result: SensorEntry[] = [];
        for (var i = 0; i <= days; ++i) {
            result.push(this.getEntry(startDate, mode));
            startDate.setUTCDate(startDate.getUTCDate() + 1);
        }

        return result;
    }

    private getAverage(date: Date): SensorEntry {
        date = DateUtil.round(date);

        var key = date.toString();
        if (this._lookupAverage[key]) {
            return this._lookupAverage[key];
        }

        var s = this.getFirst(date);

        if (!s)
            return null;

        var d = s.date;

        var result: SensorEntry = {
            index: s.index,
            date: new Date(d),
            dateKey: s.dateKey,
            time: d.getTime(),
            distance: 0,
            lat: 0,
            lng: 0,
            x: 0,
            y: 0,
            water_temperature: 0,
            temperature: 0,
            wind: 0,
            phase: null//"drift"
        };

        var entries = this.getEntriesOfDay(d);
        for (var pt of entries) {
            result.distance = Math.max(result.distance, pt.distance);
            result.lat += pt.lat;
            result.lng += pt.lng;
            result.x += pt.x;
            result.y += pt.y;
            result.temperature += pt.temperature;
            result.water_temperature += pt.water_temperature;
            result.wind += pt.wind;
        }

        if (entries.length > 1) {
            var count = entries.length;
            result.lat /= count;
            result.lng /= count;
            result.x /= count;
            result.y /= count;
            result.temperature /= count;
            result.water_temperature /= count;
            result.wind /= count;
        }

        this._lookupAverage[key] = result;

        return result;
    }

    private getEntriesOfDay(date: Date): SensorEntry[] {
        return this._lookup[DateUtil.toKey(date)] || [];
    }

    private getNearest(date: Date): SensorEntry {
        var key = date.toString();
        if (this._lookupNearest[key]) {
            return this._lookupNearest[key];
        }

        date = new Date(date);

        var now = DateUtil.fromTime(Date.now());
        date.setUTCHours(now.getUTCHours());
        date.setUTCMinutes(now.getUTCMinutes());
        date.setUTCSeconds(now.getUTCSeconds());

        var time = date.getTime();
        var diff = Number.MAX_VALUE;
        var result: SensorEntry = null;

        var entries = this.getEntriesOfDay(date);//this.entries;
        if (entries.length === 0)
            entries = this.entries;
        for (var pt of entries) {
            var d = Math.abs(pt.time - time);
            if (d < diff) {
                diff = d;
                result = pt;
            }
        }

        this._lookupNearest[key] = result;

        return result;
    }

    private getFirst(date: Date): SensorEntry {
        date = DateUtil.round(date);

        var key = date.toString();
        if (this._lookupFirst[key]) {
            return this._lookupFirst[key];
        }

        var dateKey = DateUtil.toKey(date);

        if (this._lookup[dateKey]) {
            var pt = this._lookup[dateKey][0] as SensorEntry;
            this._lookupFirst[key] = pt;
            return pt;
        }
        else {
            for (var pt of this.entries) {
                if (DateUtil.greaterEqual(pt.date, date)) {
                    this._lookupFirst[key] = pt;
                    return pt;
                }
            }
        }

        //Fallback to nearest
        var pt = this.getNearest(date);
        this._lookupFirst[key] = pt;
        return pt;
    }

    public fromData(collection: VOSensorCollection): void {
        this.data = collection;
        this.entries = [];
        this._lookup = {};
        this._lookupNearest = {};
        this._lookupFirst = {};
        this._lookupAverage = {};

        var data = collection.data;
        var index = 0;
        for (var i = 0; i < data.time.length; ++i) {
            var date = DateUtil.fromTime(data.time[i]);
            if (this.kind === "nansen") {
                //For the route the date should be normalized to "mosaic-time".
                date = DateUtil.nansenToMosaic(date);
            }

            var pt: SensorEntry = {
                index: index,
                date: date,
                dateKey: DateUtil.toKey(date),
                time: date.getTime(),
                lat: data.latitude[i],
                lng: data.longitude[i],
                x: data.x[i],
                y: data.y[i],
                distance: data.distance[i],
                water_temperature: data.water_temperature[i],
                temperature: data.temperature[i],
                wind: data.wind[i],
                phase: data.phase ? data.phase[i] : null
            };
            this.entries.push(pt);

            if (!this._lookup[pt.dateKey]) {
                this._lookup[pt.dateKey] = [pt];
            } else {
                this._lookup[pt.dateKey].push(pt);
            }

            index++;
        }

        this.onChange.dispatch();
    }

    public toJson(): string {
        var result: VOSensor = {
            kind: this.kind,
            time: [],
            distance: [],
            latitude: [],
            longitude: [],
            temperature: [],
            water_temperature: [],
            wind: [],
            x: [],
            y: [],
            phase: []
        };
        for (var pt of this.entries) {
            result.distance.push(pt.distance);
            result.latitude.push(pt.lat);
            result.longitude.push(pt.lng);
            if (this.kind === "mosaic") {
                result.temperature.push(pt.temperature);
                result.water_temperature.push(pt.water_temperature);
                result.wind.push(pt.wind);
                result.time.push(pt.time);
            }
            else {
                var dateNansen = DateUtil.mosaicToNansen(pt.date);
                result.time.push(dateNansen.getTime());
            }
            result.x.push(pt.x);
            result.y.push(pt.y);
        }

        return JSON.stringify({
            data: result
        });
    }
}